import { createModel } from '@rematch/core'
import * as firebase from 'firebase'
import * as R from 'ramda'
import { dispatch } from '../store'

export interface IAuthState {
  loading: boolean
  isAuthenticated: boolean
  showAuthModal: boolean
  error: {
    code: string
    message: string
  } | null
}

async function updateUserFromCredential(
  credential: firebase.auth.UserCredential
) {
  const currentUser = credential.user
  if (currentUser === null) {
    throw {
      code: 'auth/qraft-no-current-user',
      message: 'failed to update the current user'
    }
  }
  return dispatch.auth.updateUser(currentUser)
}

export default createModel({
  state: {
    loading: false,
    isAuthenticated: false,
    error: null
  } as IAuthState,
  reducers: {
    openAuthModal: (state: IAuthState): IAuthState => ({
      ...state,
      showAuthModal: true
    }),
    closeAuthModal: (state: IAuthState): IAuthState => ({
      ...state,
      showAuthModal: false
    }),
    authStart: (state: IAuthState): IAuthState => ({
      ...state,
      loading: true,
      isAuthenticated: false,
      error: null
    }),
    authSuccess: (state: IAuthState): IAuthState => ({
      ...state,
      loading: false,
      isAuthenticated: true,
      error: null
    }),
    authFailure: (state: IAuthState, error): IAuthState => ({
      ...state,
      loading: false,
      isAuthenticated: false,
      error
    }),
    authClear: (state: IAuthState): IAuthState => ({
      ...state,
      loading: false,
      isAuthenticated: false,
      error: null
    })
  },
  effects: {
    async logout() {
      await this.authStart()
      firebase
        .auth()
        .signOut()
        .then(this.authClear)
        .catch(this.authFailure)
    },
    async sendEmailVerification() {
      const currentUser = firebase.auth().currentUser
      if (currentUser && currentUser.email) {
        const email = currentUser.email
        await currentUser.sendEmailVerification()
        return email
      } else {
        throw {
          code: 'auth/qraft-no-current-user',
          message: 'failed to get the current user'
        }
      }
    },
    async signinWithPassword(payload: { email: string; password: string }) {
      const { email, password } = payload
      this.authStart()
      // try login first
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(updateUserFromCredential)
        .then(this.authSuccess)
        .catch(error => {
          if (error.code === 'auth/user-not-found') {
            // try register last
            firebase
              .auth()
              .createUserWithEmailAndPassword(email, password)
              .then(updateUserFromCredential)
              .then(this.authSuccess)
              .then(this.sendEmailVerification)
              .catch(this.authFailure)
          } else {
            this.authFailure(error)
          }
        })
    },
    async signinWithFacebook() {
      this.authStart()
      const provider = new firebase.auth.FacebookAuthProvider()
      firebase
        .auth()
        .signInWithPopup(provider)
        .then(this.authSuccess)
        .catch(this.authFailure)
    },
    async signinWithGoogle() {
      this.authStart()
      const provider = new firebase.auth.GoogleAuthProvider()
      firebase
        .auth()
        .signInWithPopup(provider)
        .then(this.authSuccess)
        .catch(this.authFailure)
    },
    async updateUser(user: firebase.User) {
      if (user.email === null) {
        throw {
          code: 'auth/qraft-no-current-user',
          message: 'failed to update the current user'
        }
      }
      const userSnap = await firebase
        .firestore()
        .collection('users')
        .doc(user.uid)
        .get()
      const userName = userSnap.exists
        ? userSnap.get('userName')
        : R.split('@', user.email)[0]
      const updatedUser: IUser = {
        email: user.email,
        userName,
        fullName: user.displayName || userName,
        photoURL: user.photoURL,
        title: userSnap.get('title') || null,
        description: userSnap.get('description') || null,
        jobs: userSnap.get('jobs') || []
      }
      firebase
        .firestore()
        .collection('users')
        .doc(user.uid)
        .set(updatedUser)
        .then(_ =>
          dispatch.users.updateById({ userId: user.uid, user: updatedUser })
        )
    }
  },
  selectors: {}
})



// WEBPACK FOOTER //
// ./src/models/auth.ts